import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { AskResult, ThemesResult, TranscriptSummary, ThemesEvent, AskEvent, LineId } from '../contract'
import { resolveClient, type Client } from './api'
import { navigate, useRoute, type View } from './router'
import { Reader } from './components/Reader'
import { TranscriptsView } from './views/TranscriptsView'
import { ThemesView, type RunState } from './views/ThemesView'
import { AskView } from './views/AskView'

const idle = <R,>(): RunState<R> => ({ status: 'idle', events: [], text: '' })

type ToolEv = Extract<ThemesEvent, { type: 'tool' }>
/** The API emits a tool event when the call starts and again with its output: show one row, updated. */
function mergeTool(events: ToolEv[], e: ToolEv): ToolEv[] {
  const last = events[events.length - 1]
  if (last && last.output === undefined && last.name === e.name && JSON.stringify(last.input) === JSON.stringify(e.input)) {
    return [...events.slice(0, -1), e]
  }
  return [...events, e]
}
/** Status line from streamed text: the last sentence so far, trimmed. */
function statusTail(prev: string, delta: string): string {
  const all = prev + delta
  const tail = all.slice(-200)
  const cut = tail.search(/[.!?]\s+[^.!?]*$/)
  return (cut >= 0 ? tail.slice(cut + 2) : tail).trimStart() || all
}

function usePersisted<T>(key: string, init: T): [T, (v: T | ((p: T) => T)) => void] {
  const [v, setV] = useState<T>(() => {
    try { const s = sessionStorage.getItem(key); return s ? (JSON.parse(s) as T) : init } catch { return init }
  })
  useEffect(() => { try { sessionStorage.setItem(key, JSON.stringify(v)) } catch { /* private mode */ } }, [key, v])
  return [v, setV]
}

export function App() {
  const route = useRoute()
  const [client, setClient] = useState<Client | null>(null)
  const [source, setSource] = useState<{ kind: 'api' | 'mock'; reason: string } | null>(null)
  const [transcripts, setTranscripts] = useState<TranscriptSummary[]>([])
  const [themes, setThemes] = usePersisted<RunState<ThemesResult>>('receipts.themes', idle())
  const [ask, setAsk] = usePersisted<RunState<AskResult>>('receipts.ask', idle())
  const [question, setQuestion] = useState('')
  const abort = useRef<{ themes?: AbortController; ask?: AbortController }>({})

  const reload = useCallback(async (c = client) => { if (c) setTranscripts(await c.listTranscripts()) }, [client])

  useEffect(() => {
    let live = true
    resolveClient().then(({ client: c, reason }) => {
      if (!live) return
      setClient(c); setSource({ kind: c.kind, reason })
      c.listTranscripts().then((t) => live && setTranscripts(t))
    })
    return () => { live = false }
  }, [])

  // a run that was 'running' when the page reloaded can never finish
  useEffect(() => {
    setThemes((s) => (s.status === 'running' ? idle() : s))
    setAsk((s) => (s.status === 'running' ? idle() : s))
  }, [setThemes, setAsk])

  const known = useMemo(() => new Set(transcripts.map((t) => t.id)), [transcripts])
  const titles = useMemo(() => new Map(transcripts.map((t) => [t.id, t.title])), [transcripts])

  const openLine = useCallback((id: LineId) => navigate({ ...route, t: id, r: id.slice(0, id.lastIndexOf(':')) }), [route])
  const closeReader = useCallback(() => navigate({ view: route.view }), [route.view])

  function runThemes() {
    if (!client) return
    abort.current.themes?.abort()
    const ctl = new AbortController(); abort.current.themes = ctl
    setThemes({ status: 'running', events: [], text: '' })
    client.themes(undefined, (e: ThemesEvent) => {
      if (e.type === 'tool') setThemes((s) => ({ ...s, events: mergeTool(s.events, e) }))
      else if (e.type === 'text') setThemes((s) => ({ ...s, text: statusTail(s.text, e.delta) }))
      else if (e.type === 'done') setThemes((s) => ({ ...s, status: 'done', result: e.result }))
      else setThemes((s) => ({ ...s, status: 'error', error: e.message }))
    }, ctl.signal).catch((err) => { if (!ctl.signal.aborted) setThemes((s) => ({ ...s, status: 'error', error: String(err.message ?? err) })) })
  }

  function runAsk(q: string) {
    if (!client) return
    abort.current.ask?.abort()
    const ctl = new AbortController(); abort.current.ask = ctl
    setAsk({ status: 'running', events: [], text: '' })
    client.ask(q, (e: AskEvent) => {
      if (e.type === 'tool') setAsk((s) => ({ ...s, events: mergeTool(s.events, e) }))
      else if (e.type === 'text') setAsk((s) => ({ ...s, text: s.text + e.delta }))
      else if (e.type === 'done') setAsk((s) => ({ ...s, status: 'done', result: e.result, text: '' }))
      else setAsk((s) => ({ ...s, status: 'error', error: e.message }))
    }, ctl.signal).catch((err) => { if (!ctl.signal.aborted) setAsk((s) => ({ ...s, status: 'error', error: String(err.message ?? err) })) })
  }

  const cancel = (k: 'themes' | 'ask') => {
    abort.current[k]?.abort()
    if (k === 'themes') setThemes((s) => ({ ...s, status: s.result ? 'done' : 'idle' }))
    else setAsk((s) => ({ ...s, status: s.result ? 'done' : 'idle', text: '' }))
  }

  const readerOpen = !!route.r
  const tabs: { id: View; label: string }[] = [
    { id: 'transcripts', label: 'Transcripts' },
    { id: 'themes', label: 'Themes' },
    { id: 'ask', label: 'Ask' },
  ]

  return (
    <div className={`shell${readerOpen ? ' shell--reader' : ''}`} data-view={route.view}>
      <header className="topbar">
        <a className="brand" href="#transcripts">
          <span className="brand__mark" aria-hidden>⌗</span>
          <span className="brand__name">Receipts</span>
          <span className="brand__tag">every claim has a quote, or it doesn't ship</span>
        </a>
        <nav className="tabs" aria-label="Views">
          {tabs.map((t) => (
            <a key={t.id} href={`#${t.id}`} className={`tab${route.view === t.id ? ' tab--active' : ''}`} aria-current={route.view === t.id ? 'page' : undefined} data-testid={`tab-${t.id}`}>{t.label}</a>
          ))}
        </nav>
        <div className="topbar__status">
          {source?.kind === 'mock' && <span className="chip chip--warn" title={source.reason || 'mock'} data-testid="source-chip">mock data</span>}
          {source?.kind === 'api' && <span className="chip chip--ok" data-testid="source-chip">live API</span>}
        </div>
      </header>

      <main className="content">
        {!client && <p className="empty">Connecting…</p>}
        {client && route.view === 'transcripts' && <TranscriptsView client={client} transcripts={transcripts} route={route} onAdded={() => reload()} />}
        {client && route.view === 'themes' && (
          <ThemesView run={themes} onRun={runThemes} onCancel={() => cancel('themes')} onOpenLine={openLine} knownTranscripts={known} titles={titles} />
        )}
        {client && route.view === 'ask' && (
          <AskView run={ask} question={question} setQuestion={setQuestion} onAsk={runAsk} onCancel={() => cancel('ask')} onOpenLine={openLine} knownTranscripts={known} route={route} />
        )}
      </main>

      {client && readerOpen && (
        <aside className="readerpane" data-testid="reader-pane">
          <Reader client={client} transcriptId={route.r!} lineId={route.t} route={route} onClose={closeReader} />
        </aside>
      )}
      {client && !readerOpen && route.view === 'transcripts' && (
        <aside className="readerpane readerpane--empty" aria-hidden>
          <div className="readerpane__hint">
            <div className="readerpane__glyph">¶</div>
            <p>Pick a transcript to read it here. Every line has an address, <span className="mono">#t=transcript:line</span>, that themes and answers link back to.</p>
          </div>
        </aside>
      )}
    </div>
  )
}
